// Renders the header sql_model as an editable form. Used for both add
// and edit actions in the live-preview runtime.

import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { formatForInput } from '../dateInput'
import { AlertCircle, Eye, MoreVertical, Pencil, Plus, Search } from 'lucide-react'
import { useKeyboardShortcuts } from './keyboard'
import { actionShortcutBindings, actionShortcutCombo, shortcutHint, withShortcutHint } from './actionShortcuts'
import type { ColumnDef, SqlModel, Action, ActionSurface, VisionActionSource, VisionRecordStateMap, KeyGeneration } from './types'

type VisionBuiltin = NonNullable<VisionActionSource['builtin']>
import RowActionsMenu from './list/RowActionsMenu'
import {
  actionName, actionPlacement, isExportSystemAction, isMergedSystemAction, legacyActionToDef, sortActionsByOrder,
  visionActionOf, visionNotWiredMessage, visionRunnable, visionFillsDirectly,
  visionPicksThenTransforms, visionDeletesAllLines, actionShowsOn,
  visionRecordStates, showConfirmAllows, visionShowConfirmByTitle,
  resolveReportActionParams, buildAssistantActionRequest, buildPaymentActionRequest, buildImportActionRequest, buildSendActionRequest,
  buildOpenActionRequest, buildWizardActionRequest, actionCaptureOf, withCapturedValue, isScanSessionAction,
} from './types'
import ReportActionModal from '../report/ReportActionModal'
import type { ReportRuntimeAdapter } from '../report/types'
import {
  AUDIT_TOKEN_LABELS, auditTokenOf, projectVarOf, isAutoGenerate,
  isAutoGeneratePrefixed,
  boolCheckValues, columnDocType, columnFormKey, ddlbSelectValue, hasBoolCheck, hasCheckboxValues, hasDropdown,
  hasLookup, hasRadioValues, inputType, isBoolCheckOn, isBoolCheckUnset, boolCheckOtherDisplay, textareaMinHeight, isKey, isMandatory, optionValueEquals,
  isProtect, visibleColumns, applyTextCase, isComputedColumn, isHidden,
  inlineLayout, continuationLayout, isLabelHidden, visionFieldFormat, isGeneratedKeyColumn,
  hasTextarea, hasUrlWidget, hasImageUpload, hasFileUpload, fileUploadAccept,
  isJsonColumn, isRichTextColumn, isZonedColumn,
} from './utils'
import { useTxnRuntime, type UploadResult } from './context'
import { personLabel, usePeople } from './people'
import type { RunActionResponse } from './contracts'
import LookupModal from './LookupModal'
import HtmlResultModal from './HtmlResultModal'
import SelectGridModal from './SelectGridModal'
import { useWarningConfirm } from './WarningsConfirmModal'
import { useActionCapture } from './ActionCaptureModal'
import { attemptWithWarningConfirm } from './warnings'
import {
  useActionConfirm, attemptActionWithConfirm, isActionConfirmCancelled,
} from './ActionConfirmModal'
import AutoGrowTextarea from './widgets/AutoGrowTextarea'
import UrlField from './widgets/UrlField'
import FileUploadField from './widgets/FileUploadField'
import { RichTextColumnField } from './widgets/RichTextField'
import MacCalendarIcon from './widgets/MacCalendarIcon'
import DateField from './widgets/DateField'
import { PercentField, EmailField, PhoneField, WebUrlField, NumericInput } from './widgets/FormatFields'
import { WithMinLength, minLengthPadding } from './widgets/MinLengthIndicator'
import { maxTextLength, minTextLength, textLengthForMin } from './textLength'
import { tw } from '../testHooks'
import { useTxnCustomize, type TxnCustomizeApi } from '../customize/txnContext'
import { useWizardFill } from './screen/wizardFill'
import {
  columnKey as czColumnKey, groupKey as czGroupKey, groupPath as czGroupPath,
  moveFieldDiff, moveGroupDiff, colPath as czColPath, setLeaf as czSetLeaf, resetLeaf as czResetLeaf,
} from '../customize/txnOps'
import {
  CustomizedDot, GearButton, Grip, HiddenTray, VIOLET, VIOLET_LINE, getDragData, setDragData,
} from '../customize/ui'

export function fieldAnchorId(dbName: string): string {
  return `livepreview-field-${dbName}`
}

/** Scroll a form field into view and put the cursor in it. */
export function focusFieldAnchor(dbName: string): void {
  if (typeof document === 'undefined') return
  const el = document.getElementById(fieldAnchorId(dbName))
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  const input = el.querySelector('input, select, textarea') as HTMLElement | null
  if (input) input.focus()
}

interface Props {
  model: SqlModel
  values: Record<string, unknown>
  errors: Record<string, string>
  onChange: (db_name: string, value: unknown) => void
  readOnlyKey: boolean   // when editing, PK columns are locked
  project: string
  objName: string
  // Header (main) form values. Forwarded to LookupModal so lookup
  // filters can reference {header.X} bindings from a child form.
  // For the main form itself this is the same as ``values`` (or
  // omitted — the form's own values are also passed as formValues).
  headerValues?: Record<string, unknown>
  // Runtime per-column protect override, keyed by column form key, set by an
  // item_change return ({"col": {protect: 1|0}}). 1 = protect (read-only),
  // 0 = unprotect even if the column is statically protected. A column not in
  // the map falls back to its static ``protect``.
  dynamicProtect?: Record<string, 0 | 1>
  // ── Custom action buttons ───────────────────────────────────────────────
  // The currently-open record id (edit mode) and the immediate-parent key for
  // a child form, forwarded to the run_action call so its validations /
  // business logic see the right context. ``formNo`` overrides model.form_no
  // when the host knows the effective form number.
  txnId?: string
  parentPk?: Record<string, unknown>
  formNo?: string
  // A LINE editor's context for its buttons: whether the line is new and how
  // many lines the form holds (sent with run_action — Vision line actions such
  // as DespatchAct's Default read both).
  lineContext?: { rowIsNew: boolean; lineCount: number }
  // Apply an item_change-style action result (handler 'item_change', or the
  // rows the user picked from a 'select_grid'). The host reuses its existing
  // item_change apply path. Absent ⇒ such results are ignored (buttons still
  // validate + run message / html_modal).
  onActionResult?: (res: RunActionResponse) => void
  // Perform a built-in CRUD op for a handler='system' action button.
  onSystemAction?: (op: 'add' | 'edit' | 'delete' | 'save' | 'view') => void
  // Surface a run_action error (validation failure or business-logic error)
  // using the host's error parser. Absent ⇒ falls back to notify.error.
  onActionError?: (err: unknown) => void
  // Per-button rights: hide a custom button the viewer is not granted (a
  // button some role holds BY NAME). Absent ⇒ every button renders.
  canUseAction?: (name: string) => boolean
  // Fired after an action button ran successfully, once its result has been
  // dispatched. An action's business logic may have changed the record on the
  // server (status, totals, its own editability), so hosts that show a single
  // persisted record use this to re-read it. The list screens already refresh
  // themselves and leave it unset.
  onActionDone?: (res: RunActionResponse) => void
  // View-only: the record is not editable (locked by a designer rule / awaiting
  // approval) or the user lacks edit rights. Every field is disabled, the action
  // bar is hidden, and the header shows a View indicator. The host also hides
  // its Save control. Defaults to false (normal add/edit).
  viewOnly?: boolean
  /** Draw the object's action buttons even though the form is read-only.
   *
   *  A view-only form normally hides them, and that is right for a reviewer
   *  looking at a staged record. It is wrong for a surface that shows a
   *  PERSISTED record and is the place people act on it — the process trail's
   *  record pane — where the buttons are the object's own, rights-gated by
   *  `canUseAction`, and running one is exactly what running it on the object's
   *  screen would do. */
  actionsInViewOnly?: boolean
  /** Performs an imported Vision Add / Edit / Audit Trail button on this open
   *  record. When set, those buttons show on the record (per their
   *  DISPLAY_MODE) and run through it; when omitted they stay list-only. */
  onVisionBuiltin?: (op: VisionBuiltin) => void
  // Optional explicit mode for the header indicator. When omitted it is derived:
  // viewOnly ⇒ 'view', an existing record (txnId) ⇒ 'edit', else 'add'.
  mode?: 'add' | 'edit' | 'view'
  // Human reason shown next to the View indicator (e.g. "Approved records
  // cannot be edited"). Only used in view mode.
  lockReason?: string
  // The engine's load verdict that this record's Vision workflow is in
  // progress (``wf_in_progress``) — hides its Edit and Submit buttons.
  wfInProgress?: boolean
  // Presentation layout. 'default' is the desktop multi-column, collapsible
  // group layout. 'compact' is the mobile-style layout: each group is a card,
  // always expanded (no chevron / group menu), and the custom-action bar moves
  // to the bottom. Its fields stack one per row where the card is narrow (a
  // phone, the narrow Compact window) and flow into as many columns as fit
  // where it is wide (the window maximised). Behaviour (widgets, lookups,
  // item_change, protect, mandatory) is identical.
  layout?: 'default' | 'compact'
  /** Content mounted at the end of a named group (after its fields) — the
   *  record editor's inline detail tables (`display_in_group`). A group named
   *  here is shown even with no visible header column, and starts expanded. */
  groupSlots?: Record<string, ReactNode>
  /** A one-line caption in a named group's header — why an additional-
   *  details group is asked ("Asked because Customer type is EXPORT").
   *  Presentation only; absent ⇒ no change. */
  groupCaptions?: Record<string, ReactNode>
  /** A sub-heading drawn once, before the first of `groups` in display
   *  order — "Additional details" above the attribute groups. */
  groupsHeading?: { groups: string[]; label: string }
  // The transaction's ``key_generation``. In Vision mode the engine builds the
  // key column at INSERT, so in ADD mode that field is read-only, shows
  // "Auto-generated" and has no look-up. Absent ⇒ no change.
  keyGeneration?: KeyGeneration | null
}

// Column headings of a form by every key a select_grid row may use.
function gridLabelsOf(model: SqlModel): Record<string, string> {
  const out: Record<string, string> = {}
  for (const w of model.columns || []) {
    const c = w?.column
    if (!c) continue
    const heading = String(c.heading || c.name || '')
    out[columnFormKey(c)] = heading
    if (c.name) out[String(c.name)] = heading
  }
  return out
}

// A Vision-imported form: its fields carry the legacy edit screen's x. Only
// such a form takes Vision's layout (stacked next lines, its own "Others"
// group); every other form keeps the Twasta layout. Field looks are shared.
function isVisionForm(cols: ColumnDef[]): boolean {
  return cols.some(c => String(c.screen_x ?? '').trim() !== '')
}

// On a Vision form a group keeps the name it was given ("Others" included)
// and only a field with no group at all is gathered under "Other
// Information"; elsewhere "Others" reads "Other Information" as it always has.
function formatGroupName(name: string, vision = false, settings?: GroupSettings): string {
  const custom = String(groupSetting(settings, name)?.label ?? '').trim()
  if (custom) return custom
  if (!vision && name === 'Others') return 'Other Information'
  return name.trim() || 'Other Information'
}

type GroupSettings = SqlModel['group_settings']

/** A group's settings by the key `group_settings` uses ('' → "Others"). */
export function groupSetting(
  settings: GroupSettings, name: string,
): { label?: string; order?: number | string; open?: boolean | string } | undefined {
  if (!settings) return undefined
  return settings[name.trim() || 'Others']
}

function truthySetting(v: unknown): boolean | undefined {
  if (v === undefined || v === null || v === '') return undefined
  if (typeof v === 'boolean') return v
  return ['1', 'true', 'yes', 'y'].includes(String(v).trim().toLowerCase())
}

// The legacy edit screen's x (``screen_x``) as a number; null when absent.
function screenXOf(col: ColumnDef): number | null {
  const raw = col.screen_x
  if (raw == null || String(raw).trim() === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

// Mirror PreviewPanel's ordering so live preview matches the designer:
// columns within a group sort by y (then screen_x, then original index); groups sort by
// the y of their first column (then first-appearance index). Basic stays
// pinned first; Others stays pinned last.
export function groupColumns(
  cols: ColumnDef[], vision = false, slotGroups: string[] = [], settings?: GroupSettings,
): Array<[string, ColumnDef[]]> {
  const map = new Map<string, { col: ColumnDef; index: number }[]>()
  const firstAppearance = new Map<string, number>()
  cols.forEach((col, idx) => {
    // Vision: no group at all ⇒ '' (shown as "Other Information") and a
    // group named "Others" keeps its own name. Elsewhere both are "Others".
    const g = vision ? String(col.group_name ?? '').trim() : (col.group_name || 'Others')
    if (!map.has(g)) {
      map.set(g, [])
      firstAppearance.set(g, idx)
    }
    map.get(g)!.push({ col, index: idx })
  })
  // A slot group with no visible column of its own still renders (after the
  // column groups — it has no y to sort by).
  slotGroups.forEach((g, i) => {
    if (map.has(g)) return
    map.set(g, [])
    firstAppearance.set(g, cols.length + i)
  })
  map.forEach(arr => {
    arr.sort((a, b) => {
      const dy = (Number(a.col.y) || 0) - (Number(b.col.y) || 0)
      if (dy !== 0) return dy
      // Same row: the legacy screen's left-to-right order (``screen_x``),
      // when both fields carry one.
      const ax = screenXOf(a.col)
      const bx = screenXOf(b.col)
      if (ax != null && bx != null && ax !== bx) return ax - bx
      return a.index - b.index
    })
  })
  const groupPos = new Map<string, { y: number; idx: number }>()
  map.forEach((arr, name) => {
    const firstY = arr.length ? (Number(arr[0].col.y) || 0) : Number.MAX_SAFE_INTEGER
    groupPos.set(name, { y: firstY, idx: firstAppearance.get(name) ?? 0 })
  })
  const entries: Array<[string, ColumnDef[]]> = [...map.entries()]
    .map(([name, arr]) => [name, arr.map(x => x.col)])
  entries.sort((a, b) => {
    if (a[0] === 'Basic Information' || a[0] === 'Basic') return -1
    if (b[0] === 'Basic Information' || b[0] === 'Basic') return 1
    const lastA = a[0] === '' || a[0] === 'Others' || a[0] === 'Other Information'
    const lastB = b[0] === '' || b[0] === 'Others' || b[0] === 'Other Information'
    if (lastA !== lastB) return lastA ? 1 : -1
    const pa = groupPos.get(a[0])!
    const pb = groupPos.get(b[0])!
    if (pa.y !== pb.y) return pa.y - pb.y
    return pa.idx - pb.idx
  })
  // An explicit `group_settings.order` (authored, or set per organization)
  // moves a group; a group without one keeps its derived position.
  if (settings && entries.some(([n]) => groupSetting(settings, n)?.order != null)) {
    const rank = (n: string, i: number) => {
      const o = Number(groupSetting(settings, n)?.order)
      return Number.isFinite(o) && String(groupSetting(settings, n)?.order ?? '').trim() !== ''
        ? o : (i + 1) * 10
    }
    return entries
      .map((e, i) => ({ e, i, r: rank(e[0], i) }))
      .sort((x, y) => (x.r - y.r) || (x.i - y.i))
      .map(x => x.e)
  }
  return entries
}

export default function FormRenderer({
  model, values, errors, onChange, readOnlyKey, project, objName,
  headerValues, dynamicProtect,
  txnId, parentPk, formNo, lineContext, onActionResult, onSystemAction, onActionError,
  onActionDone, viewOnly, actionsInViewOnly, mode, layout = 'default', canUseAction,
  onVisionBuiltin, groupSlots, groupCaptions, groupsHeading, keyGeneration, wfInProgress,
}: Props) {
  const compact = layout === 'compact'
  const cz = useTxnCustomize()
  const cols = visibleColumns(model)
  const visionForm = useMemo(() => isVisionForm(cols), [cols])
  const slotKey = groupSlots ? Object.keys(groupSlots).join('|') : ''
  const groupSettings = model.group_settings
  const groups = useMemo(
    () => groupColumns(cols, visionForm, slotKey ? slotKey.split('|') : [], groupSettings),
    [cols, visionForm, slotKey, groupSettings],
  )
  // The image/document upload transport is host-provided (designer →
  // document store / site-doc; standalone runtime → its own crud endpoint).
  const uploadFn: (file: File) => Promise<UploadResult> = useTxnRuntime().uploadDocument

  // First group expanded, others collapsed. Re-initialised whenever the
  // set of group names changes (e.g. switching forms). Derive-during-
  // render via setState + ref so the new model never renders with
  // all groups collapsed for one frame.
  const groupKeysSig = groups.map(([n]) => n).join('|')
  // A group hosting an inline table also starts open — its rows ARE the data —
  // as does any the form names in `open_groups`.
  const initialExpanded = () => {
    const init: Record<string, boolean> = {}
    const open = new Set(model.open_groups || [])
    groups.forEach(([name], idx) => {
      // An explicit `group_settings.<g>.open` wins (true or false).
      const pinned = truthySetting(groupSetting(groupSettings, name)?.open)
      init[name] = pinned ?? (idx === 0 || !!groupSlots?.[name] || open.has(name))
    })
    return init
  }
  const [expanded, setExpanded] = useState<Record<string, boolean>>(initialExpanded)
  const lastKeysSig = useRef(groupKeysSig)
  if (lastKeysSig.current !== groupKeysSig) {
    lastKeysSig.current = groupKeysSig
    setExpanded(initialExpanded())
  }

  // Auto-expand any group whose fields currently have errors so the
  // ValidationBanner's scroll-to-field can actually reach them.
  useEffect(() => {
    const errorKeys = Object.keys(errors)
    if (errorKeys.length === 0) return
    const toOpen = new Set<string>()
    for (const [name, gcols] of groups) {
      if (gcols.some(c => errors[columnFormKey(c)])) toOpen.add(name)
    }
    if (toOpen.size === 0) return
    setExpanded(prev => {
      let changed = false
      const next = { ...prev }
      toOpen.forEach(n => {
        if (!next[n]) { next[n] = true; changed = true }
      })
      return changed ? next : prev
    })
  }, [errors, groups])

  const expandAll = () => {
    const next: Record<string, boolean> = {}
    groups.forEach(([name]) => { next[name] = true })
    setExpanded(next)
  }
  const collapseOthers = (keepName: string) => {
    const next: Record<string, boolean> = {}
    groups.forEach(([name]) => { next[name] = name === keepName })
    setExpanded(next)
  }
  const toggleGroup = (name: string) => {
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }))
  }

  // ── Keyboard shortcuts (scoped to this form) ───────────────────────────────
  // Ctrl+E toggles between "all groups expanded" and "only the first (Basic)
  // group expanded"; F2 opens the lookup picker for the field that currently
  // has focus. Scoped to rootRef so when several forms are mounted (header +
  // child tabs, or a lookup's inline Add form) only the focused/hovered one
  // responds.
  const rootRef = useRef<HTMLDivElement>(null)
  const allExpanded = groups.length > 0 && groups.every(([n]) => expanded[n])
  const toggleExpandMode = () => {
    if (allExpanded) collapseOthers(groups[0]?.[0] || '')
    else expandAll()
  }
  const openFocusedLookup = () => {
    const root = rootRef.current
    if (!root) return
    const active = document.activeElement as HTMLElement | null
    const dbName = active?.getAttribute('data-livepreview-field')
    if (!dbName) return
    const esc = (s: string) =>
      (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(s) : s.replace(/"/g, '\\"')
    const btn = root.querySelector<HTMLButtonElement>(
      `button[data-lookup-trigger="${esc(dbName)}"]`,
    )
    if (btn && !btn.disabled) btn.click()
  }
  useKeyboardShortcuts(
    [
      { combo: 'ctrl+e', run: toggleExpandMode },
      { combo: 'f2', run: openFocusedLookup },
    ],
    { scopeRef: rootRef },
  )

  if (cols.length === 0) {
    return (
      <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: '0.9625rem' }}>
        No columns defined on this form. Add a column in the designer to start
        capturing data.
      </div>
    )
  }

  // The mode this record is open in decides which imported Vision buttons
  // show (their DISPLAY_MODE). A read-only form otherwise hides the bar, but
  // a Vision button set to show in view mode is still drawn there.
  const surface: ActionSurface = mode ?? (viewOnly ? 'view' : txnId ? 'edit' : 'add')
  // The record's confirmed / cancelled / closed state (header forms of an
  // imported Vision screen) gates its buttons by OBJ_ACTIONS.SHOW_CONFIRM;
  // a workflow in progress hides Edit / Submit.
  const recordStates = visionRecordStates(model, values,
    { saved: surface !== 'add', inWorkflow: wfInProgress })
  const showConfirmByTitle = visionShowConfirmByTitle(model.actions)
  const visionInView = !!viewOnly && (model.actions || []).some(a =>
    visionActionOf(legacyActionToDef(a)) && actionShowsOn(legacyActionToDef(a), 'view')
      && showConfirmAllows(legacyActionToDef(a), recordStates, showConfirmByTitle))
  const actionBar = (!viewOnly || actionsInViewOnly || visionInView) ? (
    <RunActionBar
      actions={model.actions || []}
      surface={surface}
      recordStates={recordStates}
      visionOnly={!!viewOnly && !actionsInViewOnly}
      onVisionBuiltin={onVisionBuiltin}
      gridLabels={gridLabelsOf(model)}
      project={project}
      objName={objName}
      formNo={formNo || model.form_no || '1'}
      values={values}
      headerValues={headerValues}
      txnId={txnId}
      parentPk={parentPk}
      lineContext={lineContext}
      onActionResult={onActionResult}
      onSystemAction={onSystemAction}
      onActionError={onActionError}
      onActionDone={onActionDone}
      canUseAction={canUseAction}
      // Bottom-anchored buttons in compact layout (Android parity).
      layout={layout}
      shortcutScope={rootRef}
    />
  ) : null

  // Customize mode (the organization's functional administrator re-presenting
  // this form): fields gain a drag handle + ⚙, groups can be renamed and
  // moved, hidden fields sit in a tray. Off for everyone else.
  const czForm = cz && !compact ? (formNo || model.form_no || '1') : null
  const czGroupOrder = groups.map(([g]) => g)
  const czMoveField = (dragData: string, toGroup: string, beforeKey: string | null) => {
    if (!cz || !czForm) return
    const [kind, fno, key] = dragData.split('|')
    if (kind !== 'field' || fno !== czForm || key === beforeKey) return
    const all = (model.columns || []).map(w => w.column).filter(Boolean)
    const moving = all.find(c => czColumnKey(c) === key)
    if (!moving) return
    const diff = moveFieldDiff({
      formNo: czForm, groups, settings: model.group_settings, moving, toGroup, beforeKey,
    })
    if (isHidden(moving)) diff.set.push({ path: czColPath(czForm, key, 'hidden'), value: false })
    void cz.session.apply(diff, `Move ${moving.heading || moving.name}`)
  }
  const czHiddenCols = czForm
    // Additional attributes (and their hidden deciding values) are never
    // overlay leaves — they are set up under Additional attributes.
    ? (model.columns || []).map(w => w.column).filter(c => c && isHidden(c) && !c.uda)
    : []

  return (
    <div ref={rootRef} {...tw('form', formNo ?? '1', { mode })}
         style={{ padding: compact ? '8px 0 16px' : '12px 0' }}>
      {/* Default layout: action bar on top. Compact: it moves below the groups. */}
      {!compact && actionBar}
      {groups.map(([groupName, gcols]) => (
        <Fragment key={groupName}>
        {groupsHeading && groupName === groups.find(([g]) => groupsHeading.groups.includes(g))?.[0] && (
          <div style={{
            fontSize: 13, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase',
            color: 'var(--color-text-secondary)', padding: compact ? '12px 16px 4px' : '14px 24px 6px',
          }}>{groupsHeading.label}</div>
        )}
        <GroupBlock
          key={groupName}
          groupSettings={groupSettings}
          customize={czForm && cz ? {
            api: cz, formNo: czForm, groupOrder: czGroupOrder,
            onDropField: (data, beforeKey) => czMoveField(data, groupName, beforeKey),
          } : undefined}
          groupName={groupName}
          columns={gcols}
          expanded={!!expanded[groupName]}
          onToggle={() => toggleGroup(groupName)}
          onExpandAll={expandAll}
          onCollapseOthers={() => collapseOthers(groupName)}
          values={values}
          errors={errors}
          onChange={onChange}
          readOnlyKey={readOnlyKey}
          viewOnly={viewOnly}
          project={project}
          objName={objName}
          headerValues={headerValues}
          dynamicProtect={dynamicProtect}
          uploadDocument={uploadFn}
          compact={compact}
          slot={groupSlots?.[groupName]}
          caption={groupCaptions?.[groupName]}
          generatedKeyInAdd={surface === 'add' ? keyGeneration : null}
          visionForm={visionForm}
        />
        </Fragment>
      ))}
      {compact && actionBar}
      {czForm && cz && (
        <HiddenTray
          title="Hidden on this form"
          hint="Drag a field here to hide it on the form. It can still be a grid column."
          items={czHiddenCols.map(c => ({
            key: czColumnKey(c),
            label: String(c.heading || c.name),
            onShow: () => void cz.session.apply(
              czSetLeaf(czColPath(czForm, czColumnKey(c), 'hidden'), false), `Show ${c.heading || c.name}`),
          }))}
          onDropHide={(data) => {
            const [kind, fno, key] = data.split('|')
            if (kind !== 'field' || fno !== czForm) return
            const c = (model.columns || []).map(w => w.column).find(x => x && czColumnKey(x) === key)
            if (c && !isMandatory(c)) {
              void cz.session.apply(czSetLeaf(czColPath(czForm, key, 'hidden'), true), `Hide ${c.heading || c.name}`)
            } else if (c) {
              cz.openField(czForm, key, rootRef.current as HTMLElement)
            }
          }}
        />
      )}
    </div>
  )
}

// ── Add / Edit / View indicator badge ───────────────────────────────────────
// A compact chip the modal HOSTS drop into their header (not a separate row in
// the form body). In view mode hovering reveals why the record is locked
// (lockReason) as a tooltip, so the body stays uncluttered.
export function FormModeBadge({
  mode, lockReason,
}: {
  mode: 'add' | 'edit' | 'view'
  lockReason?: string
}) {
  const cfg = {
    add: { Icon: Plus, label: 'Add', color: '#16a34a', bg: 'rgba(22,163,74,0.12)' },
    edit: { Icon: Pencil, label: 'Edit', color: '#2563eb', bg: 'rgba(37,99,235,0.12)' },
    view: { Icon: Eye, label: 'View only', color: '#b45309', bg: 'rgba(180,83,9,0.12)' },
  }[mode]
  const { Icon } = cfg
  return (
    <span
      title={mode === 'view' ? (lockReason || 'This record is read-only') : `${cfg.label} mode`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px', borderRadius: 12,
        background: cfg.bg, color: cfg.color,
        fontSize: '0.8125rem', fontWeight: 700, letterSpacing: 0.2,
        whiteSpace: 'nowrap', cursor: 'default',
      }}
    >
      <Icon size={13} />
      {cfg.label}
    </span>
  )
}

// ── Custom action buttons ──────────────────────────────────────────────────
// Renders the form's action buttons below the title. On click the engine runs
// the action's PRE-action validations (a failure rejects → surfaced via
// onActionError / notify.error and the action does NOT run); on success it
// dispatches by handler: system → onSystemAction; item_change → onActionResult;
// message → toast; html_modal → HtmlResultModal; select_grid → SelectGridModal
// whose picked rows are applied like a multi-row item_change.
export function RunActionBar({
  actions, project, objName, formNo, values, headerValues, txnId, parentPk, lineContext,
  onActionResult, onSystemAction, onActionError, onActionDone, layout = 'default',
  canUseAction, surface, recordStates, visionOnly = false, onVisionBuiltin, gridLabels,
  include, inline = false, onVisionDeleteAll, shortcutScope,
}: {
  actions: Action[]
  /** The form this bar belongs to: the buttons' keyboard shortcuts
   *  (`ActionDef.shortcut`) fire while focus is in it or the pointer is over
   *  it, so a header and a line form never both answer one key. Absent ⇒ the
   *  bar's buttons have no keys here (a line grid's inline toolbar). */
  shortcutScope?: React.RefObject<HTMLElement | null>
  /** Only the actions this keeps (the line grid's toolbar: Vision page-context 1 buttons). */
  include?: (a: Action) => boolean
  /** A Vision "Delete All" button (SERVICE_HANDLER 'D') deletes every line of
   *  the form; only a host that can do that (the line grid) passes this, and
   *  the button shows nowhere else. */
  onVisionDeleteAll?: () => void
  /** Laid out inline in a toolbar, not as the form's own button row. */
  inline?: boolean
  /** The mode the record is open in — filters Vision buttons by DISPLAY_MODE. */
  surface: ActionSurface
  /** The open record's Vision state — filters buttons by SHOW_CONFIRM. */
  recordStates?: VisionRecordStateMap
  /** Only imported Vision buttons (a read-only form drawing its view-mode ones). */
  visionOnly?: boolean
  onVisionBuiltin?: (op: VisionBuiltin) => void
  gridLabels?: Record<string, string>
  project: string
  objName: string
  formNo: string
  values: Record<string, unknown>
  headerValues?: Record<string, unknown>
  txnId?: string
  parentPk?: Record<string, unknown>
  lineContext?: { rowIsNew: boolean; lineCount: number }
  onActionResult?: (res: RunActionResponse) => void
  onSystemAction?: (op: 'add' | 'edit' | 'delete' | 'save' | 'view') => void
  onActionError?: (err: unknown) => void
  onActionDone?: (res: RunActionResponse) => void
  layout?: 'default' | 'compact'
  canUseAction?: (name: string) => boolean
}) {
  const runtime = useTxnRuntime()
  const wizardFill = useWizardFill()
  // Make changes: each button gets a ⚙, and the bar ends with "+ Add a
  // button" (never in Customize, never in a line grid's own toolbar).
  const czBuild = useTxnCustomize()
  const czButtons = czBuild?.openButton && !inline ? czBuild : null
  const [busy, setBusy] = useState<string | null>(null)
  const [html, setHtml] = useState<{ title: string; html: string } | null>(null)
  // ``pick``: a Vision pick-list button — the ticked rows go back for its
  // transform instead of being applied as they are.
  const [grid, setGrid] = useState<{ title: string; res: RunActionResponse; pick?: boolean } | null>(null)
  const [report, setReport] = useState<{
    title: string
    objName: string
    adapter: ReportRuntimeAdapter
    params: Record<string, unknown>
  } | null>(null)
  const { confirmWarnings, warningsModal } = useWarningConfirm()
  const { confirmAction, actionConfirmModal } = useActionConfirm()
  const { captureFor, actionCaptureModal } = useActionCapture()

  // Hide disabled actions; keep only those with a name — rendered in the
  // designer-specified order. System add / edit actions never render as
  // standalone buttons anywhere: they merge into the list's built-in Add
  // button / Edit control (label + hover text + pre-action validations), so
  // the open-record bar drops them too instead of duplicating the built-ins.
  // A button some role has been granted BY NAME is hidden from viewers who do
  // not hold it (the server refuses it too — this only spares them the 403).
  const seenBuiltins = new Set<string>()
  const showConfirmByTitle = visionShowConfirmByTitle(actions)
  const visible = sortActionsByOrder(actions).filter(a => {
    const def = legacyActionToDef(a)
    const builtin = visionActionOf(def)?.builtin
    return !def.disabled && (def.name || '').trim() && !isMergedSystemAction(def)
      // The Export system action is the screen's Export control, not a button.
      && !isExportSystemAction(def)
      // An imported Vision Add / Edit / Audit Trail shows on an open record
      // only when the host can perform it there; never one that would do
      // nothing (Edit while editing, Add while adding, Edit / Audit Trail on a
      // record not saved yet), and once each (Vision apps often carry two Edit
      // rows).
      && (!builtin || (!!onVisionBuiltin && builtin !== surface
        && (surface !== 'add' || builtin === 'add')))
      && actionShowsOn(def, surface)
      && showConfirmAllows(def, recordStates || {}, showConfirmByTitle)
      && (!visionOnly || !!visionActionOf(def))
      && (!canUseAction || canUseAction(def.name))
      && (!builtin || (!seenBuiltins.has(builtin) && !!seenBuiltins.add(builtin)))
      && (!include || include(a))
      && (!visionDeletesAllLines(def) || !!onVisionDeleteAll)
      // A scan session runs from its line form's toolbar (ChildFormTab).
      && !isScanSessionAction(def)
  })
  // The buttons' own keys (ActionDef.shortcut) — the same buttons the bar
  // shows, run exactly as a click. Never while Make changes is setting them
  // up, never on an inline toolbar, never while one is already running.
  const runRef = useRef<(name: string) => void>(() => {})
  useKeyboardShortcuts(
    actionShortcutBindings(visible).map(b => ({
      combo: b.combo,
      run: () => { if (!busy) runRef.current(b.name) },
    })),
    { enabled: !!shortcutScope && !inline && !czButtons, scopeRef: shortcutScope, sameDialog: true },
  )

  if (visible.length === 0 && !czButtons) return null
  // A button sits on the bar only when the designer put it there; the rest
  // share a ⋮ at the end of the bar (actionPlacement — default the menu). In
  // Make changes every button stays visible so each can be set up.
  const onBar = (a: typeof visible[number]) => !!czButtons || actionPlacement(legacyActionToDef(a)) === 'bar'
  const barVisible = visible.filter(onBar)
  const menuVisible = visible.filter(a => !onBar(a))

  const applyGridSelection = (res: RunActionResponse, selected: Array<Record<string, unknown>>) => {
    // Hand the picked rows back to the host (which knows the form tree) to
    // apply like a multi-row item_change. ``rows`` is narrowed to the
    // selection; the host routes them to the right (child) form.
    onActionResult?.({ ...res, handler: 'select_grid', rows: selected })
  }

  // A Vision pick-list button's second step: the ticked rows, exactly as the
  // bean listed them, go back with the same request; the engine runs the
  // button's transform over them and answers with the lines to add.
  const transformPicked = async (name: string, selected: Array<Record<string, unknown>>) => {
    if (selected.length === 0) return
    setBusy(name)
    try {
      const res = await attemptWithWarningConfirm(
        (ignoreWarnings) => runtime.runAction(project, objName, {
          formNo, actionName: name, header: values, headerValues,
          txnId, parentPk,
          ...(lineContext ? lineContext : {}),
          selectedRows: selected,
          ...(ignoreWarnings.length ? { ignoreWarnings } : {}),
        }),
        confirmWarnings,
      )
      const notifyInfo = runtime.notify.info ?? runtime.notify.success
      res.prompts?.forEach(p => notifyInfo(p.message))
      if (res.rows?.length) applyGridSelection(res, res.rows)
      else runtime.notify.success('No lines to add')
    } catch (err) {
      if (onActionError) onActionError(err)
      else runtime.notify.error('Action failed')
    } finally {
      setBusy(null)
    }
  }

  const run = async (name: string) => {
    // An imported Vision button with no Twasta implementation yet: say so
    // instead of calling an action that has nothing to run.
    const clicked = visible.find(a => legacyActionToDef(a).name === name)
    const vision = visionActionOf(clicked)
    if (onVisionDeleteAll && visionDeletesAllLines(clicked)) {
      onVisionDeleteAll()
      return
    }
    if (vision?.builtin && onVisionBuiltin) {
      onVisionBuiltin(vision.builtin)
      return
    }
    if (vision && !visionRunnable(clicked)) {
      ;(runtime.notify.info ?? runtime.notify.success)(visionNotWiredMessage(name, vision))
      return
    }
    // A button that asks for a value first (a scan / typed code): cancelling
    // the prompt runs nothing.
    const clickedDef = clicked ? legacyActionToDef(clicked) : undefined
    const capture = actionCaptureOf(clickedDef)
    let posted = values
    if (capture) {
      const captured = await captureFor(clickedDef)
      if (!captured) return
      posted = withCapturedValue(values, capture, captured)
    }
    setBusy(name)
    try {
      // A 422 of warnings-only (VALIDATION_WARNINGS) opens the confirm modal;
      // proceeding re-runs the action with the acknowledged codes so the
      // engine + business logic skip those warnings (iBase forcedFlag).
      // A configured confirmation (handler='confirm') gates the effect: the
      // confirm loop wraps the warning-confirm retry so a post-confirm warning
      // still prompts. attemptActionWithConfirm returns the final, non-confirm
      // response (or rejects with the cancel sentinel).
      const res = await attemptActionWithConfirm(
        (confirm) => attemptWithWarningConfirm(
          (ignoreWarnings) => runtime.runAction(project, objName, {
            formNo, actionName: name, header: posted, headerValues,
            txnId, parentPk,
            ...(lineContext ? lineContext : {}),
            ...(ignoreWarnings.length ? { ignoreWarnings } : {}),
            ...(confirm ? { confirm } : {}),
          }),
          confirmWarnings,
        ),
        confirmAction,
      )
      // Informational prompts (severity 'P') — shown for every handler.
      const notifyInfo = runtime.notify.info ?? runtime.notify.success
      res.prompts?.forEach(p => notifyInfo(p.message))
      switch (res.handler) {
        case 'system':
          if (res.system_op) onSystemAction?.(res.system_op)
          break
        case 'message':
          // A bare "Done" only when no prompt already reported the outcome.
          if (res.message || !res.prompts?.length) runtime.notify.success(res.message || 'Done')
          break
        case 'html_modal':
          setHtml({ title: name, html: res.html || '' })
          break
        case 'report': {
          // Open the bound Jasper report with params resolved from the open
          // record's values. Engine echoes the binding; fall back to the
          // action's own def for older engine responses.
          const def = visible.map(legacyActionToDef).find(d => d.name === name)
          const rep = (res.report?.obj_name ? res.report : def?.report) || undefined
          const reportObj = (rep?.obj_name || '').trim()
          if (!reportObj) {
            runtime.notify.error(`Action "${name}" has no report bound to it.`)
            break
          }
          const make = runtime.makeReportAdapter
          if (!make) {
            runtime.notify.error('Report viewing is not available on this surface.')
            break
          }
          setReport({
            title: name,
            objName: reportObj,
            adapter: make(reportObj),
            params: resolveReportActionParams(rep?.param_map, values),
          })
          break
        }
        case 'assistant': {
          // Call the in-app Assistant with values from THIS form ({form.X})
          // and the parent/header form ({header.X}). Engine echoes the
          // binding; fall back to the action's own def for older engines.
          const def = visible.map(legacyActionToDef).find(d => d.name === name)
          const binding = (res.assistant?.mode || res.assistant?.intent_id || res.assistant?.prompt)
            ? res.assistant : def?.assistant
          const open = runtime.openAssistantAction
          if (!open) {
            runtime.notify.error('The Assistant is not available on this surface.')
            break
          }
          const req = buildAssistantActionRequest(name, binding, values, headerValues)
          if (typeof req === 'string') {
            runtime.notify.error(req)
            break
          }
          open(req)
          break
        }
        case 'payment': {
          // Open the application's own checkout for the record on screen.
          // The engine echoes the binding; fall back to the action's own def
          // for an older engine. `txnId` is what makes this a payment for a
          // SAVED record — an unsaved form has nothing to charge for yet.
          const def = visible.map(legacyActionToDef).find(d => d.name === name)
          const binding = (res.payment?.amount_column
            ? res.payment : def?.payment) || undefined
          const open = runtime.openPaymentAction
          if (!open) {
            runtime.notify.error('Payments are not available on this surface.')
            break
          }
          const req = buildPaymentActionRequest(name, binding, objName, txnId)
          if (typeof req === 'string') {
            runtime.notify.error(req)
            break
          }
          open(req)
          break
        }
        case 'import': {
          // Import a file into this object in the host's import window.
          const def = visible.map(legacyActionToDef).find(d => d.name === name)
          const open = runtime.openImportAction
          if (!open) {
            runtime.notify.error('Importing files is not available on this surface.')
            break
          }
          open(buildImportActionRequest(name, res.import || def?.import, objName))
          break
        }
        case 'send': {
          // Open the send window over THIS record (a not-yet-saved record has
          // nothing to send yet — the window says so).
          const def = visible.map(legacyActionToDef).find(d => d.name === name)
          const open = runtime.openSendAction
          if (!open) {
            runtime.notify.error('Sending messages is not available on this surface.')
            break
          }
          open(buildSendActionRequest(name, res.send || def?.send, objName, txnId ? [txnId] : []))
          break
        }
        case 'open': {
          // Open ANOTHER object with values from THIS form ({form.X}) and its
          // header ({header.X}). A report opens in this form's report window;
          // the host opens everything else (it owns navigation / the drawer).
          const def = visible.map(legacyActionToDef).find(d => d.name === name)
          const binding = (res.open?.obj_name ? res.open : def?.open) || undefined
          const req = buildOpenActionRequest(name, binding, objName, values, headerValues ?? values)
          if (typeof req === 'string') {
            runtime.notify.error(req)
            break
          }
          if (req.objType === 'R') {
            const make = runtime.makeReportAdapter
            if (!make) {
              runtime.notify.error('Report viewing is not available on this surface.')
              break
            }
            setReport({ title: req.title, objName: req.objName, adapter: make(req.objName), params: req.params })
            break
          }
          const open = runtime.openObjectAction
          if (!open) {
            runtime.notify.error('Opening another screen is not available on this surface.')
            break
          }
          open(req)
          break
        }
        case 'wizard': {
          // Start a Transaction Wizard with inputs from THIS form ({form.X})
          // and its header ({header.X}). The host mounts the runner.
          const def = visible.map(legacyActionToDef).find(d => d.name === name)
          const binding = (res.wizard?.obj_name ? res.wizard : def?.wizard) || undefined
          const open = runtime.openWizardAction
          if (!open) {
            runtime.notify.error('Wizards are not available on this surface.')
            break
          }
          const req = buildWizardActionRequest(name, binding, values, headerValues ?? values)
          if (typeof req === 'string') {
            runtime.notify.error(req)
            break
          }
          // A `fill` wizard works on this open record and hands its values
          // back here; the runner ignores both for any other mode.
          if (wizardFill && req.presentation === 'drawer') {
            req.fill = { seed: wizardFill.seed(), onFill: wizardFill.apply }
          }
          open(req)
          break
        }
        case 'select_grid':
          // A Vision SERVICE_HANDLER 3 button fills the form with every row
          // the bean returned, as Vision does — no pick list.
          if (visionFillsDirectly(clicked)) {
            if (res.rows?.length) applyGridSelection(res, res.rows)
            else runtime.notify.success('No lines to add')
          } else {
            setGrid({ title: name, res, pick: visionPicksThenTransforms(clicked) })
          }
          break
        case 'item_change':
        default:
          if (res.message) runtime.notify.success(res.message)
          if (res.warnings?.length) {
            res.warnings.forEach(w => runtime.notify.error(w.message))
          } else if (res.warning) {
            runtime.notify.error(res.warning)
          }
          onActionResult?.(res)
          break
      }
      // The action ran and its writes are committed — let the host re-read the
      // record it changed (a 'system' op already re-opens/reloads on its own).
      if (res.handler !== 'system') onActionDone?.(res)
    } catch (err) {
      if (isActionConfirmCancelled(err)) return  // user backed out of the gate
      if (onActionError) onActionError(err)
      else runtime.notify.error('Action failed')
    } finally {
      setBusy(null)
    }
  }

  runRef.current = (name: string) => { void run(name) }
  const hintOf = (name: string) => {
    const a = visible.find(x => actionName(x) === name)
    return a ? actionShortcutCombo(a) : null
  }

  return (
    <div style={inline ? {
      display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
    } : layout === 'compact' ? {
      // Compact: bottom-anchored button row below the form groups.
      display: 'flex', gap: 8, flexWrap: 'wrap',
      padding: '4px 16px 8px',
    } : {
      // Aligned with the group headers and fields below (24px), so the first
      // button starts on the form's own left edge instead of the window's.
      display: 'flex', gap: 8, flexWrap: 'wrap',
      padding: '0 24px 12px', marginBottom: 4,
    }}>
      {barVisible.map((a, i) => {
        const name = actionName(a)
        const running = busy === name
        const button = (
          <button
            key={`${name}-${i}`}
            disabled={!!busy}
            {...tw('action', name, {
              handler: typeof a === 'string' ? undefined : a.handler, form: formNo, running,
            })}
            onClick={() => run(name)}
            title={inline ? undefined : (hintOf(name) ? withShortcutHint(name, hintOf(name)) : undefined)}
            style={{
              padding: '5px 14px', fontSize: '0.8425rem', borderRadius: 14,
              border: '1px solid var(--color-accent, #4c8dff)',
              background: running ? 'var(--color-accent, #4c8dff)' : 'transparent',
              color: running ? '#fff' : 'var(--color-accent, #4c8dff)',
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy && !running ? 0.5 : 1,
            }}>
            {running ? 'Running…' : name}
          </button>
        )
        if (!czButtons) return button
        return (
          <span key={`${name}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {button}
            <GearButton label={`Settings for the ${name} button`}
              active={czButtons.openButtonKey === `${formNo}|${name}`}
              onClick={(el) => czButtons.openButton?.(formNo, name, el)} />
          </span>
        )
      })}
      {menuVisible.length > 0 && (
        <span title="More actions" style={{ display: 'inline-flex', alignItems: 'center' }}
          {...tw('action', 'record-actions-menu', { form: formNo })}>
          <RowActionsMenu circled actions={menuVisible.map((a) => {
            const name = actionName(a)
            return {
              label: busy === name ? 'Running…' : name, kind: 'run' as const, hook: name,
              shortcut: inline ? undefined : (shortcutHint(hintOf(name)) || undefined),
              onClick: () => { if (!busy) void run(name) },
            }
          })} />
        </span>
      )}
      {czButtons && (
        <>
          {visible.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', alignSelf: 'center' }}>No buttons yet.</span>
          )}
          <button type="button" onClick={() => czButtons.addButton?.(formNo)} {...tw('action', 'make-changes-add-button')}
            style={{
              padding: '5px 14px', fontSize: '0.8425rem', borderRadius: 14, cursor: 'pointer',
              border: `1px dashed ${VIOLET}`, background: 'transparent', color: VIOLET, fontWeight: 600,
            }}>+ Add a button</button>
        </>
      )}

      {html && (
        <HtmlResultModal
          title={html.title}
          html={html.html}
          onClose={() => setHtml(null)}
        />
      )}
      {report && (
        <ReportActionModal
          title={report.title}
          project={project}
          objName={report.objName}
          adapter={report.adapter}
          params={report.params}
          onClose={() => setReport(null)}
        />
      )}
      {grid && (
        <SelectGridModal
          title={grid.title}
          rows={grid.res.rows || []}
          columns={grid.res.columns}
          labels={gridLabels}
          onCancel={() => setGrid(null)}
          onApply={(selected) => {
            if (grid.pick) void transformPicked(grid.title, selected)
            else applyGridSelection(grid.res, selected)
            setGrid(null)
          }}
        />
      )}
      {warningsModal}
      {actionConfirmModal}
      {actionCaptureModal}
    </div>
  )
}

function GroupBlock({
  groupName, columns, expanded, onToggle, onExpandAll, onCollapseOthers,
  values, errors, onChange, readOnlyKey, viewOnly, project, objName, headerValues,
  dynamicProtect, uploadDocument, compact = false, slot, caption, generatedKeyInAdd, visionForm = false,
  groupSettings, customize,
}: {
  groupSettings?: SqlModel['group_settings']
  customize?: {
    api: TxnCustomizeApi
    formNo: string
    groupOrder: string[]
    /** A dragged field was dropped before `beforeKey` (null = at the end). */
    onDropField: (dragData: string, beforeKey: string | null) => void
  }
  groupName: string
  columns: ColumnDef[]
  expanded: boolean
  onToggle: () => void
  onExpandAll: () => void
  onCollapseOthers: () => void
  values: Record<string, unknown>
  errors: Record<string, string>
  onChange: (db_name: string, value: unknown) => void
  readOnlyKey: boolean
  viewOnly?: boolean
  project: string
  objName: string
  headerValues?: Record<string, unknown>
  dynamicProtect?: Record<string, 0 | 1>
  uploadDocument: (file: File) => Promise<UploadResult>
  compact?: boolean
  slot?: ReactNode
  caption?: ReactNode
  // Set only while adding: the key generation whose key column is built by
  // the engine (see FormRenderer's ``keyGeneration``).
  generatedKeyInAdd?: KeyGeneration | null
  // The whole form is Vision-imported (see isVisionForm).
  visionForm?: boolean
}) {
  // A Vision-imported group: its fields carry the edit screen's x.
  const visionFlow = columns.some(c => String(c.screen_x ?? '').trim() !== '')
  const errorCount = columns.reduce(
    (n, c) => n + (errors[columnFormKey(c)] ? 1 : 0), 0)
  const [renaming, setRenaming] = useState(false)
  // Where a dragged field would land: beside a field (its left half = before
  // it, right half = after it), or `null` = the end-of-group strip.
  const [dropBefore, setDropBefore] = useState<string | null | undefined>(undefined)
  const [dropSide, setDropSide] = useState<'before' | 'after'>('before')
  const cz = customize
  const czLabel = String(groupSettings?.[czGroupKey(groupName)]?.label ?? '').trim()
  const czLabelPath = cz ? czGroupPath(cz.formNo, groupName, 'label') : ''
  const czRenamed = !!cz && cz.api.session.isCustomized(czLabelPath)
  const commitRename = (v: string) => {
    setRenaming(false)
    if (!cz) return
    const next = v.trim()
    const standard = formatGroupName(groupName, visionForm)
    if (!next || next === standard) {
      if (czRenamed) void cz.api.session.apply(czResetLeaf(czLabelPath), `Rename ${standard}`)
      return
    }
    if (next !== czLabel) void cz.api.session.apply(czSetLeaf(czLabelPath, next), `Rename ${standard}`)
  }
  const czMenu = cz ? (() => {
    const i = cz.groupOrder.indexOf(groupName)
    const openPath = czGroupPath(cz.formNo, groupName, 'open')
    const startsOpen = groupSettings?.[czGroupKey(groupName)]?.open
    const prefix = czGroupPath(cz.formNo, groupName, 'x').slice(0, -2)
    return [
      { label: 'Rename group', onClick: () => setRenaming(true) },
      { label: 'Move group up', disabled: i <= 0, onClick: () => void cz.api.session.apply(moveGroupDiff(cz.formNo, cz.groupOrder, groupName, -1), 'Move group') },
      { label: 'Move group down', disabled: i < 0 || i >= cz.groupOrder.length - 1, onClick: () => void cz.api.session.apply(moveGroupDiff(cz.formNo, cz.groupOrder, groupName, 1), 'Move group') },
      {
        label: String(startsOpen) === 'false' || startsOpen === false ? 'Start expanded' : 'Start collapsed',
        onClick: () => void cz.api.session.apply(
          czSetLeaf(openPath, String(startsOpen) === 'false' || startsOpen === false), 'Group start state'),
      },
      // Make changes writes the screen itself: there is no "standard" to go
      // back to (Undo and Versions do that).
      ...(cz.api.scope === 'app' ? [] : [
        { label: 'Reset this group', disabled: !cz.api.session.countCustomized(prefix), onClick: () => void cz.api.session.resetUnder([prefix], 'Reset group') },
      ]),
    ]
  })() : undefined
  const fieldDrag = (col: ColumnDef, inner: ReactNode) => {
    if (!cz) return inner
    const key = czColumnKey(col)
    const customized = cz.api.session.countCustomized(czColPath(cz.formNo, key, 'x').slice(0, -2)) > 0
    const open = cz.api.openFieldKey === `${cz.formNo}|${key}`
    return (
      <div
        key={`cz-${key}`}
        onDragOver={(e) => {
          e.preventDefault(); e.stopPropagation()
          const r = e.currentTarget.getBoundingClientRect()
          setDropBefore(key)
          setDropSide(e.clientX > r.left + r.width / 2 ? 'after' : 'before')
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropBefore(undefined)
        }}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation()
          const r = e.currentTarget.getBoundingClientRect()
          const after = e.clientX > r.left + r.width / 2
          setDropBefore(undefined)
          const data = getDragData(e)
          if (!after) { cz.onDropField(data, key); return }
          // After this field = before the next one in the group (skipping the
          // field being dragged), or the end of the group when it is the last.
          const movingKey = data.split('|')[2]
          const order = columns.map(c => czColumnKey(c)).filter(k => k !== movingKey)
          const i = order.indexOf(key)
          cz.onDropField(data, i >= 0 && i + 1 < order.length ? order[i + 1] : null)
        }}
        style={{
          position: 'relative', minWidth: 0, padding: '6px 34px 8px 8px', borderRadius: 7,
          border: `1px ${open ? 'solid' : 'dashed'} ${open ? '#7c3aed' : VIOLET_LINE}`,
          background: '#fdfcff',
          boxShadow: dropBefore === key
            ? `inset ${dropSide === 'after' ? '-4px' : '4px'} 0 0 ${VIOLET}`
            : undefined,
        }}
      >
        {/* Only the grip drags: the field itself stays a working field (the
            record can be typed into and saved while customizing). An
            additional attribute is placed by its own setup, not dragged into
            an overlay; its ⚙ says where that is. */}
        <span draggable={!col.uda}
          onDragStart={(e) => {
            if (col.uda) return
            setDragData(e, `field|${cz.formNo}|${key}`)
            const box = e.currentTarget.parentElement
            if (box) e.dataTransfer.setDragImage(box, 12, 12)
          }}
          style={{ position: 'absolute', left: 2, top: 8, display: 'flex' }}><Grip size={12} /></span>
        <span style={{ position: 'absolute', top: 5, right: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
          {customized && <CustomizedDot />}
          <GearButton label={`Change ${col.heading || col.name}`} active={open}
            onClick={(el) => cz.api.openField(cz.formNo, key, el)} />
        </span>
        <div style={{ paddingLeft: 10 }}>{inner}</div>
      </div>
    )
  }
  return (
    <div style={compact ? {
      // Compact: each group is a card in a single-column stack.
      border: '1px solid var(--color-border)',
      borderRadius: 12,
      margin: '12px',
      overflow: 'hidden',
      background: 'var(--lp-input-bg, var(--color-bg))',
    } : {
      borderBottom: '1px solid var(--color-border)',
      // The data-entry body is a distinct light surface: white in the light
      // theme, dark (with light text) in the dark theme — `--lp-input-bg`
      // tracks the theme so it adapts. The group header below keeps the theme's
      // secondary colour so it blends rather than showing a hard grey.
      background: 'var(--lp-input-bg, var(--color-bg))',
    }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: compact ? '10px 16px' : '8px 24px',
          background: 'var(--color-surface-2, var(--color-bg-secondary))',
          borderBottom: expanded ? '1px solid var(--color-border)' : 'none',
          fontSize: compact ? '0.74rem' : '0.78rem',
          fontWeight: compact ? 800 : 600,
          letterSpacing: compact ? 0.3 : undefined,
          textTransform: compact ? 'uppercase' : undefined,
          color: 'var(--color-text-secondary)',
          userSelect: 'none',
        }}
      >
        {/* Collapsible header — same toggle + group menu (Expand all / Collapse
            others) in both layouts; compact only adjusts typography. */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          {...tw('group', groupName || 'default')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            flex: 1, minWidth: 0,
            background: 'transparent', border: 'none', padding: 0,
            cursor: 'pointer', color: 'inherit', font: 'inherit',
            textAlign: 'left',
          }}
        >
          {cz && <Grip size={13} title="Move the group from its ⋮ menu" />}
          <span style={{ fontSize: '0.8125rem' }}>{expanded ? '▼' : '▶'}</span>
          {renaming && cz
            ? <input autoFocus defaultValue={formatGroupName(groupName, visionForm, groupSettings)}
                aria-label="Group name"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation()
                  if (e.key === 'Enter') commitRename((e.target as HTMLInputElement).value)
                  if (e.key === 'Escape') setRenaming(false)
                }}
                onBlur={(e) => commitRename(e.target.value)}
                style={{ height: 26, width: 240, border: `2px solid ${VIOLET}`, borderRadius: 5, padding: '0 8px', font: 'inherit', fontWeight: 600, color: 'var(--color-text)' }} />
            : <span>{formatGroupName(groupName, visionForm, groupSettings)}</span>}
          {cz && !renaming && (
            <span role="button" tabIndex={0} aria-label="Rename group" title="Rename group"
              onClick={(e) => { e.stopPropagation(); setRenaming(true) }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setRenaming(true) } }}
              style={{ color: VIOLET, display: 'inline-flex', marginLeft: 2 }}><Pencil size={12} /></span>
          )}
          {cz && czRenamed && !renaming && (
            <span style={{ fontWeight: 400, fontSize: '0.8125rem', color: VIOLET, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <CustomizedDot />was “{formatGroupName(groupName, visionForm)}”
            </span>
          )}
          {errorCount > 0 && (
            <span
              title={`${errorCount} field${errorCount === 1 ? '' : 's'} with errors`}
              style={{
                marginLeft: 6,
                padding: '1px 7px',
                borderRadius: 10,
                background: 'rgba(239,68,68,0.15)',
                color: '#ef4444',
                fontSize: '0.8125rem',
                fontWeight: 700,
              }}
            >{errorCount}</span>
          )}
          {caption && (
            <span style={{
              marginLeft: 8, fontSize: 13, fontWeight: 400, color: 'var(--color-text-secondary)',
              textTransform: 'none', letterSpacing: 'normal', minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{caption}</span>
          )}
        </button>
        <GroupMenu
          onExpandAll={onExpandAll}
          onCollapseOthers={onCollapseOthers}
          extra={czMenu}
        />
      </div>
      {expanded && columns.length > 0 && (
        // A Vision-imported screen (its fields carry the edit screen's x)
        // flows like Vision's: up to three columns filled top to bottom, so
        // the group reads in the screen's order. Everything else keeps the
        // row-major auto-fit grid.
        <div style={visionFlow && !compact ? {
          columnWidth: 280,
          columnCount: 3,
          columnGap: 22,
          padding: 24,
        } : {
          display: 'grid',
          // Both layouts fit as many 280px columns as the width holds: one on a
          // phone or a narrow window, more once there is room.
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
          gap: compact ? '14px' : '16px 22px',
          padding: compact ? 16 : 24,
        }}>
          {(() => {
            const inline = inlineLayout(columns)
            const { besides } = inline
            // Caption-less next lines (ADDR2 / ADDR3) stack under their field
            // — on a Vision form only.
            const { owners, below } = visionForm
              ? continuationLayout(columns, inline.owners)
              : { owners: inline.owners, below: new Map<string, ColumnDef[]>() }
            const row = (col: ColumnDef, hideLabel?: boolean, continuation?: boolean) => fieldDrag(col,
              <FieldRow
                key={col.col_id || col.name}
                col={col}
                hideLabel={hideLabel}
                continuation={continuation}
                value={values[columnFormKey(col)]}
                error={errors[columnFormKey(col)]}
                onChange={(v) => onChange(columnFormKey(col), v)}
                readOnlyKey={readOnlyKey}
                viewOnly={viewOnly}
                project={project}
                objName={objName}
                formValues={values}
                headerValues={headerValues}
                dynamicProtect={dynamicProtect}
                uploadDocument={uploadDocument}
                generatedKey={isGeneratedKeyColumn(col, generatedKeyInAdd)}
              />,
            )
            const cell = (col: ColumnDef, continuation?: boolean) => {
              const beside = besides.get(columnFormKey(col))
              if (!beside?.length) return row(col, false, continuation)
              if (visionFlow) {
                // Vision draws a field's partners (CEO under its prefix,
                // Tele 2/3 under Tele 1, a code's description) UNDER it, so
                // the field keeps its full width.
                const shown = beside
                return (
                  <div
                    key={col.col_id || col.name}
                    style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}
                  >
                    {row(col, false, continuation)}
                    {shown.map(b => (
                      <div key={b.col_id || b.name} style={{ minWidth: 0 }}>{row(b, true, true)}</div>
                    ))}
                  </div>
                )
              }
              // Fields drawn beside this one (a description next to its
              // code) share its cell, on the same line, with no caption.
              return (
                <div
                  key={col.col_id || col.name}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minWidth: 0 }}
                >
                  <div style={{ flex: '1 1 110px', minWidth: 0 }}>{row(col, false, continuation)}</div>
                  {beside.map(b => (
                    <div key={b.col_id || b.name} style={{ flex: '1.5 1 120px', minWidth: 0 }}>
                      {row(b, true, continuation)}
                    </div>
                  ))}
                </div>
              )
            }
            const cells = owners.map(col => {
              const under = below.get(columnFormKey(col))
              if (!under?.length) return cell(col)
              return (
                <div
                  key={`stack-${col.col_id || col.name}`}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}
                >
                  {cell(col)}
                  {under.map(c => cell(c, true))}
                </div>
              )
            })
            if (!visionFlow || compact) return cells
            // In column flow a cell must not split across two columns, and
            // the vertical gap the grid gave is a bottom margin here.
            return cells.map((c, i) => (
              <div key={`vcell-${i}`} style={{ breakInside: 'avoid', marginBottom: 16, minWidth: 0 }}>
                {c}
              </div>
            ))
          })()}
        </div>
      )}
      {expanded && cz && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDropBefore(null) }}
          onDragLeave={() => setDropBefore(undefined)}
          onDrop={(e) => { e.preventDefault(); setDropBefore(undefined); cz.onDropField(getDragData(e), null) }}
          style={{
            margin: '0 24px 16px', marginTop: columns.length ? -8 : 16, height: 34, borderRadius: 7,
            border: `1px dashed ${dropBefore === null ? VIOLET : 'var(--color-border)'}`,
            background: dropBefore === null ? '#f5f3ff' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, color: 'var(--color-text-secondary)',
          }}
        >Drop here to put it at the end of this group</div>
      )}
      {expanded && cz?.api.addField && (
        <div style={{ margin: '0 24px 14px', display: 'flex' }}>
          <button type="button" onClick={() => cz.api.addField?.(cz.formNo, groupName)}
            {...tw('action', `make-changes-add-field:${groupName || 'default'}`)}
            style={{
              border: `1px dashed ${VIOLET}`, borderRadius: 7, background: '#f5f3ff', color: VIOLET,
              height: 32, padding: '0 14px', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
            }}>+ Add a field here</button>
        </div>
      )}
      {expanded && slot && (
        <div style={{
          padding: compact ? '0 16px 16px' : '0 24px 20px',
          paddingTop: columns.length > 0 ? 0 : (compact ? 16 : 20),
        }}>{slot}</div>
      )}
    </div>
  )
}

function GroupMenu({
  onExpandAll, onCollapseOthers, extra,
}: {
  onExpandAll: () => void
  onCollapseOthers: () => void
  /** Customize mode: Rename / Move up / Move down / Start collapsed / Reset. */
  extra?: { label: string; onClick: () => void; disabled?: boolean }[]
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        title="Group options"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 24, height: 24, padding: 0,
          background: 'transparent', border: 'none', borderRadius: 4,
          cursor: 'pointer', color: 'var(--color-text-secondary)',
        }}
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: 'var(--color-bg, #ffffff)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            padding: 4, minWidth: 180,
            zIndex: 1000,
          }}
        >
          <MenuItem label="Expand all"
            onClick={() => { onExpandAll(); setOpen(false) }} />
          <MenuItem label="Collapse others"
            onClick={() => { onCollapseOthers(); setOpen(false) }} />
          {extra && extra.length > 0 && (
            <>
              <div style={{ height: 1, background: 'var(--color-border, #e5e7eb)', margin: '4px 4px' }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: VIOLET, textTransform: 'uppercase', letterSpacing: '.06em', padding: '4px 10px' }}>Customize</div>
              {extra.map((it) => (
                <MenuItem key={it.label} label={it.label} disabled={it.disabled}
                  onClick={() => { if (!it.disabled) { it.onClick(); setOpen(false) } }} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function MenuItem({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'block', width: '100%',
        padding: '7px 10px',
        fontSize: '0.9375rem', textAlign: 'left',
        background: 'transparent',
        color: 'var(--color-text, #111827)',
        border: 'none', borderRadius: 4, cursor: 'pointer',
        lineHeight: 1.2, fontWeight: 500,
        opacity: disabled ? 0.45 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--color-bg-secondary, #f3f4f6)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent'
      }}
    >{label}</button>
  )
}

function FieldRow({
  col, hideLabel, value, error, onChange: onChangeRaw, readOnlyKey, viewOnly, project, objName,
  formValues, headerValues, dynamicProtect, uploadDocument, continuation, generatedKey,
}: {
  col: ColumnDef
  // Drawn beside another field (inline_with): no caption of its own.
  hideLabel?: boolean
  // The next line of the field above it (stacked in its cell): no caption
  // line at all, not even a blank one.
  continuation?: boolean
  // The engine generates this key column at INSERT (adding a record under a
  // Vision ``key_generation``): read-only, "Auto-generated", no look-up.
  generatedKey?: boolean
  value: unknown
  error?: string
  onChange: (v: unknown) => void
  readOnlyKey: boolean
  viewOnly?: boolean
  project: string
  objName: string
  formValues?: Record<string, unknown>
  headerValues?: Record<string, unknown>
  dynamicProtect?: Record<string, 0 | 1>
  uploadDocument: (file: File) => Promise<UploadResult>
}) {
  const label = col.heading || col.name
  const noCaption = !!continuation || !!hideLabel || isLabelHidden(col)
  // Typed text takes the column's case (``text_case``) as it is entered, in
  // every widget below — a text box, a look-up box, a text area.
  const onChange = (v: unknown) => onChangeRaw(typeof v === 'string' ? applyTextCase(col, v) : v)
  const mandatory = isMandatory(col)
  // Editability depends purely on protect (and the PK-when-editing
  // case). Tokens never lock the field — they only annotate the source
  // of the value. Audit tokens are stamped server-side regardless of
  // what the form posts; project variables are pre-filled at form
  // open but remain user-overridable when not protected.
  const auditToken = auditTokenOf(col)
  const projectVar = projectVarOf(col)
  const autoGen = isAutoGenerate(col)
  const autoLabel = generatedKey
    ? 'Auto-generated key (assigned on save)'
    : auditToken
    ? AUDIT_TOKEN_LABELS[auditToken]
    : projectVar
      ? `Project variable :${projectVar}`
      : isAutoGeneratePrefixed(col)
        // Its prefix comes from other fields on this form, so say that — the
        // number only appears once those are filled in.
        ? 'Auto-generated id (built from this form’s data on save)'
        : autoGen
          ? 'Auto-generated serial (incremented on save)'
          : null
  // Runtime protect override from an item_change return wins over the static
  // column.protect: 1 protects, 0 unprotects; absent → static protect.
  const dynProtect = dynamicProtect?.[columnFormKey(col)]
  // A computed column is never editable, whatever an item change says.
  const protectedNow = isComputedColumn(col) ? true
    : dynProtect === 1 ? true
      : dynProtect === 0 ? false
        : isProtect(col)
  // View-only locks every field regardless of its static/dynamic protect.
  const disabled = viewOnly || protectedNow || (readOnlyKey && isKey(col)) || !!generatedKey
  const type = inputType(col)
  // Form-state key — used as the field's anchor ID and data-attr so the
  // validation banner (which receives form_key in error.field) can scroll
  // to and highlight the right input.
  const dbName = columnFormKey(col)
  // The person a protected user-key column points at, when the engine named
  // them. Null on an editable field — see the input below.
  const people = usePeople()
  const personName = disabled ? personLabel(people, dbName, value) : null
  // A key the engine generates has nothing to look up.
  const showLookup = hasLookup(col) && !hasDropdown(col) && !generatedKey
  // Audit tokens depend on save-time context (now, current user, IP)
  // so we can't pre-fill them — show a hint instead. auto_generate()
  // columns are filled server-side at INSERT by reading MAX(col)+1.
  // Project-variable columns are pre-filled by LivePreviewTab.startAdd.
  const placeholder = generatedKey
    ? 'Auto-generated'
    : auditToken
    ? 'set automatically'
    : autoGen
      ? 'auto-generated on save'
      : (col.default_value || '')
  const [lookupOpen, setLookupOpen] = useState(false)

  // A rich-text editor must not sit inside a <label>: a label forwards every
  // click to its first labelable descendant, which here would be a toolbar
  // button. It also takes the full row — a 280px column is no place to write.
  const rich = isRichTextColumn(col)
  // Nor may a switch / radio set / checkbox set: the label would forward a
  // click anywhere in the row to the switch (flipping it) or to the first
  // radio. Those controls name themselves from the caption via
  // aria-labelledby instead.
  const choice = !rich && !hasDropdown(col)
    && (hasRadioValues(col) || hasCheckboxValues(col) || hasBoolCheck(col))
  const Wrap = rich || choice ? 'div' : 'label'
  const captionId = `${fieldAnchorId(dbName)}-caption`
  // The caption is blank (or absent) when the field draws none, so the
  // control is then named by aria-label.
  const nameProps = noCaption
    ? { 'aria-label': label }
    : { 'aria-labelledby': captionId }
  const vfmt = visionFieldFormat(col)
  // A Yes / No switch sits on its caption's line, at the field's right edge
  // (Vision draws "Return Allowed:" left and its switch flush right).
  const inlineToggle = !rich && !noCaption && !continuation
    && hasBoolCheck(col) && col.edit_mask?.style === 'toggle'

  // Optional `min_length`: a live counter inside the field while it can be
  // typed into. Absent on the column ⇒ nothing changes.
  const minLen = disabled ? null : minTextLength(col)
  const typedLen = minLen != null ? textLengthForMin(col, value) : 0
  const minPad = minLen != null ? minLengthPadding(minLen) : 0

  return (
    <Wrap
      id={fieldAnchorId(dbName)}
      {...tw('field', dbName, {
        col: col.db_name, disabled, mandatory, error: !!error, lookup: showLookup,
      })}
      title={noCaption ? label : undefined}
      style={{
        ...(rich ? { gridColumn: '1 / -1' } : {}),
        display: 'flex',
        ...(inlineToggle
          ? { flexDirection: 'row' as const, flexWrap: 'wrap' as const, alignItems: 'center', gap: 10 }
          : { flexDirection: 'column' as const, gap: 4 }),
        padding: error ? '8px 10px' : 0,
        margin: error ? '-8px -10px' : 0,
        background: error ? 'rgba(239,68,68,0.06)' : 'transparent',
        borderRadius: error ? 6 : 0,
        border: error ? '1px solid rgba(239,68,68,0.35)' : '1px solid transparent',
        transition: 'background 0.15s',
      }}
    >
      {!continuation && <span
        id={captionId}
        aria-hidden={noCaption || undefined}
        style={{
          fontSize: '0.8125rem',
          color: error ? '#ef4444' : 'var(--color-text-secondary)',
          fontWeight: error ? 600 : 500,
          letterSpacing: 0.2,
          // No caption (hide_label / drawn beside another field): the line
          // keeps its height so the input lines up with its neighbours.
          ...(noCaption ? { visibility: 'hidden' as const } : {}),
        }}
      >
        {noCaption ? '\u00a0' : label}
        {!noCaption && mandatory && !generatedKey && <span style={{ color: '#ef4444' }}> *</span>}
        {autoLabel && (
          <span
            title={autoLabel}
            style={{
              marginLeft: 6,
              padding: '1px 6px',
              borderRadius: 10,
              background: 'var(--color-accent-weak, rgba(76,141,255,0.16))',
              color: 'var(--color-accent)',
              fontSize: '0.8125rem',
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}
          >Auto</span>
        )}
      </span>}
      {rich ? (
        <WithMinLength min={minLen} count={typedLen} placement="corner">
          <RichTextColumnField
            project={project}
            objName={objName}
            value={value}
            onChange={v => onChange(v)}
            disabled={disabled}
            placeholder={placeholder || undefined}
            hasError={!!error}
            fieldKey={dbName}
          />
        </WithMinLength>
      ) : hasDropdown(col) ? (
        <select
          value={ddlbSelectValue(value, col.edit_mask.values)}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          style={fieldStyle(error, disabled)}
          data-livepreview-field={dbName}
        >
          <option value="">— Select —</option>
          {col.edit_mask.values.map((opt, i) => (
            <option key={`${opt.data}-${i}`} value={opt.data}>{opt.display}</option>
          ))}
        </select>
      ) : hasRadioValues(col) ? (
        <div
          role="radiogroup"
          {...nameProps}
          style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', paddingTop: 4 }}
          data-livepreview-field={dbName}
        >
          {col.edit_mask?.style === 'chips' && (
            <style>{'.lp-radio-chip:focus-within{outline:2px solid var(--pg-primary,#2563eb);outline-offset:2px}'}</style>
          )}
          {col.edit_mask.values.map((opt, i) => {
            const on = optionValueEquals(value, opt.data)
            const chips = col.edit_mask?.style === 'chips'
            return (
              <label
                key={`${opt.data}-${i}`}
                className={chips ? 'lp-radio-chip' : undefined}
                style={chips ? {
                  position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6,
                  minHeight: 40, padding: '0 16px', borderRadius: 999, fontSize: '0.9625rem',
                  fontWeight: on ? 600 : 500, cursor: disabled ? 'not-allowed' : 'pointer',
                  border: `1.5px solid ${on ? 'var(--pg-primary, var(--color-accent))' : 'var(--pg-border, var(--color-border))'}`,
                  background: on ? 'var(--pg-primary, var(--color-accent))' : 'var(--pg-surface, var(--color-bg))',
                  color: on ? 'var(--pg-on-primary, #fff)' : 'var(--pg-text, var(--color-text))',
                  opacity: disabled ? 0.6 : 1,
                } : {
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: '0.9625rem', cursor: disabled ? 'not-allowed' : 'pointer',
                  color: 'var(--color-text)',
                }}
              >
                <input
                  type="radio"
                  name={`livepreview-radio-${dbName}`}
                  value={opt.data}
                  checked={on}
                  onChange={() => onChange(opt.data)}
                  disabled={disabled}
                  style={chips ? { position: 'absolute', opacity: 0, width: 1, height: 1, margin: 0 } : undefined}
                />
                {/* A colour swatch per value (an additional attribute's
                    `pictures.by_value`). */}
                {col.pictures?.by_value?.[String(opt.data)]?.swatch && (
                  <span aria-hidden style={{
                    width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                    background: col.pictures.by_value[String(opt.data)].swatch,
                    border: '1px solid var(--color-border-strong)',
                  }} />
                )}
                {opt.display}
              </label>
            )
          })}
        </div>
      ) : hasCheckboxValues(col) ? (
        <div
          role="group"
          {...nameProps}
          style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', paddingTop: 4 }}
          data-livepreview-field={dbName}
        >
          {col.edit_mask.values.map((opt, i) => (
            <CheckboxOption
              key={`${opt.data}-${i}`}
              optData={opt.data}
              optDisplay={opt.display}
              value={value}
              disabled={disabled}
              onChange={onChange}
            />
          ))}
        </div>
      ) : hasBoolCheck(col) ? (
        (() => {
          const { checked: checkedData, unchecked: uncheckedData } = boolCheckValues(col)
          const isOn = isBoolCheckOn(value, checkedData)
          if (col.edit_mask?.style === 'toggle') {
            const vals = col.edit_mask.values || []
            // No value yet ⇒ neither Yes nor No: a neutral switch the first
            // click turns on. The stored value is untouched until then.
            // ...nor is a value from the list's other entries (Item's Active
            // "On Alert"): shown by name in the neutral state, never as No.
            const other = boolCheckOtherDisplay(col, value)
            const unset = isBoolCheckUnset(value) || other != null
            return (
              <ToggleSwitch
                on={unset ? null : isOn}
                label={other != null ? other : unset ? '—'
                  : String((isOn ? vals[0]?.display : vals[1]?.display) ?? (isOn ? 'Yes' : 'No'))}
                disabled={disabled}
                onChange={on => onChange(on ? checkedData : uncheckedData)}
                dbName={dbName}
                captionId={noCaption ? undefined : captionId}
                caption={label}
                alignEnd={inlineToggle}
              />
            )
          }
          return (
            <input
              type="checkbox"
              {...nameProps}
              checked={isOn}
              onChange={e => onChange(e.target.checked ? checkedData : uncheckedData)}
              disabled={disabled}
              style={{ width: 18, height: 18 }}
              data-livepreview-field={dbName}
            />
          )
        })()
      ) : hasTextarea(col) ? (
        <WithMinLength min={minLen} count={typedLen} placement="corner">
          <AutoGrowTextarea
            value={value == null ? '' : String(value)}
            placeholder={placeholder}
            minHeight={textareaMinHeight(col)}
            maxLength={maxTextLength(col) ?? undefined}
            disabled={disabled}
            onChange={v => onChange(v)}
            style={{
              ...fieldStyle(error, disabled),
              ...(minLen != null ? { paddingBottom: 26 } : {}),
            }}
            data-livepreview-field={dbName}
          />
        </WithMinLength>
      ) : hasUrlWidget(col) ? (
        <UrlField
          value={value == null ? '' : String(value)}
          placeholder={placeholder || 'https://…'}
          maxLength={maxTextLength(col) ?? undefined}
          disabled={disabled}
          onChange={v => onChange(v)}
          style={fieldStyle(error, disabled)}
          data-livepreview-field={dbName}
        />
      ) : hasFileUpload(col) ? (
        <FileUploadField
          kind={hasImageUpload(col) ? 'image' : 'document'}
          value={value == null ? '' : String(value)}
          onChange={v => onChange(v)}
          uploadFn={uploadDocument}
          accept={fileUploadAccept(col)}
          disabled={disabled}
          fieldKey={dbName}
          docType={columnDocType(col) || undefined}
        />
      ) : isJsonColumn(col) ? (
        (() => {
          // JSON/JSONB column: monospace auto-grow editor. Objects arriving
          // from the engine are pretty-printed; the value round-trips as a
          // string (the engine's save coercion validates/serialises it).
          const text = value == null ? ''
            : typeof value === 'object' ? JSON.stringify(value, null, 2)
            : String(value)
          let invalid = false
          if (text.trim()) {
            try { JSON.parse(text) } catch { invalid = true }
          }
          return (
            <>
              <AutoGrowTextarea
                value={text}
                placeholder={placeholder || '{ }'}
                disabled={disabled}
                onChange={v => onChange(v)}
                style={{
                  ...fieldStyle(invalid ? 'invalid JSON' : error, disabled),
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.9375rem',
                }}
                data-livepreview-field={dbName}
              />
              {invalid && (
                <span style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: '0.8125rem', color: '#f59e0b', fontWeight: 500, marginTop: 2,
                }}>
                  <AlertCircle size={12} /> Not valid JSON yet
                </span>
              )}
            </>
          )
        })()
      ) : type === 'checkbox' ? (
        <input
          type="checkbox"
          checked={value === true || value === 'true' || value === 1}
          onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          style={{ width: 18, height: 18 }}
          data-livepreview-field={dbName}
        />
      ) : showLookup ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <WithMinLength min={minLen} count={typedLen} placement="inline" style={{ flex: 1 }}>
            <input
              type="text"
              value={value == null ? '' : String(value)}
              placeholder={placeholder || (disabled ? undefined : 'Click 🔍 to look up…')}
              maxLength={maxTextLength(col) ?? undefined}
              onChange={e => onChange(e.target.value)}
              disabled={disabled}
              style={{
                ...fieldStyle(error, disabled), flex: 1,
                ...(minLen != null ? { paddingRight: minPad } : {}),
              }}
              data-livepreview-field={dbName}
            />
          </WithMinLength>
          {/* A protected field has no PopHelp (Vision shows none either). */}
          {!disabled && (
            <button
              type="button"
              onClick={() => { if (!disabled) setLookupOpen(true) }}
              disabled={disabled}
              data-lookup-trigger={dbName}
              {...tw('action', 'lookup', { field: dbName })}
              className="btn btn-ghost btn-sm"
              title={`Open lookup (F2): ${col.lookup}`}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--color-border)',
                borderRadius: 4,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <Search size={14} />
            </button>
          )}
          <LookupModal
            open={lookupOpen}
            project={project}
            objName={objName}
            lookup={col.lookup}
            fieldLabel={label}
            title={col.lookup_title}
            addObject={col.lookup_add_object}
            dataCol={col.lookup_sql_data_col}
            onClose={() => setLookupOpen(false)}
            onSelect={(row) => onChange(row.data)}
            // Vision MULTI_OPT above 0 (its client reads any positive value
            // as multi — customer's Reminder Types is 2): tick several rows;
            // the field keeps them comma-joined (pre-ticked on reopen).
            multi={Number(col.lookup_multi) > 0}
            selectedValues={String(value ?? '').split(',').map(s => s.trim()).filter(Boolean)}
            onSelectMany={(rows) => {
              onChange(rows.map(r => (r.data == null ? '' : String(r.data))).filter(Boolean).join(','))
              setLookupOpen(false)
            }}
            filter={col.lookup_filter}
            filterRules={col.lookup_filter_rules}
            lookupSql={col.lookup_sql}
            field={col.name}
            inheritedField={col.lookup_inherited_from?.field_name}
            formValues={formValues}
            initialQuery={value == null ? '' : String(value)}
            headerValues={headerValues}
          />
        </div>
      ) : (type === 'date' || type === 'datetime-local') ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MacCalendarIcon value={formatForInput(value, type) || value} />
          <DateField
            type={type}
            value={value}
            onChange={v => onChange(v)}
            zoned={isZonedColumn(col)}
            disabled={disabled}
            style={{ ...fieldStyle(error, disabled), flex: 1 }}
            data-livepreview-field={dbName}
          />
        </div>
      ) : vfmt === 'percentage' ? (
        <PercentField
          value={value}
          onChange={v => onChange(v)}
          placeholder={placeholder}
          label={label}
          disabled={disabled}
          style={fieldStyle(error, disabled)}
          fieldKey={dbName}
        />
      ) : (vfmt === 'email' || vfmt === 'phone' || vfmt === 'weburl') ? (
        (() => {
          // Vision [Email] / [Phone] / [WebUrl]: an icon inside the field
          // sends a mail, dials, or opens the page — still usable when the
          // field is read-only, as Vision's link is in view mode.
          const LinkWidget = vfmt === 'email' ? EmailField
            : vfmt === 'phone' ? PhoneField : WebUrlField
          return (
            <LinkWidget
              value={value == null ? '' : String(value)}
              onChange={v => onChange(v)}
              placeholder={placeholder}
              maxLength={maxTextLength(col) ?? undefined}
              disabled={disabled}
              style={fieldStyle(error, disabled)}
              fieldKey={dbName}
            />
          )
        })()
      ) : personName ? (
        // A user-key column the form cannot edit reads as the PERSON it points
        // at (see `txn/people`). Only when disabled: an editable key field
        // keeps showing the key, because a name typed into it would not be one.
        <input
          type="text"
          value={personName}
          disabled
          style={fieldStyle(error, true)}
          data-livepreview-field={dbName}
        />
      ) : type === 'number' ? (
        // A number is typed as TEXT with a decimal keypad: no spinner, and
        // the mouse wheel scrolls the form instead of changing the value.
        <NumericInput
          value={value}
          placeholder={placeholder}
          onChange={v => onChange(v)}
          disabled={disabled}
          style={{ ...fieldStyle(error, disabled), textAlign: 'right' }}
          data-livepreview-field={dbName}
        />
      ) : (
        // A minimum only means something for typed TEXT — never a number.
        <WithMinLength min={minLen} count={typedLen} placement="inline" show={type === 'text'}>
          <input
            type={type}
            value={formatForInput(value, type)}
            placeholder={placeholder}
            maxLength={maxTextLength(col) ?? undefined}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            style={{
              ...fieldStyle(error, disabled),
              ...(minLen != null && type === 'text' ? { paddingRight: minPad } : {}),
            }}
            data-livepreview-field={dbName}
          />
        </WithMinLength>
      )}
      {error && (
        <span style={{
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: '0.8125rem', color: '#ef4444',
          fontWeight: 500, marginTop: 2,
          // Below the caption + switch line, not beside the switch.
          ...(inlineToggle ? { flexBasis: '100%' } : {}),
        }}>
          <AlertCircle size={12} />
          {error}
        </span>
      )}
    </Wrap>
  )
}

// Renders one checkbox in a checkbox-group edit_mask. The aggregate value
// is stored as a comma-separated string of selected `data` codes — chosen
// for simplicity so the column can stay typed as VARCHAR without schema
// changes.
function CheckboxOption({
  optData, optDisplay, value, disabled, onChange,
}: {
  optData: string
  optDisplay: string
  value: unknown
  disabled: boolean
  onChange: (v: unknown) => void
}) {
  const selected = parseCsv(value)
  const isOn = selected.includes(String(optData))
  const toggle = (checked: boolean) => {
    const next = new Set(selected)
    if (checked) next.add(String(optData))
    else next.delete(String(optData))
    onChange(Array.from(next).join(','))
  }
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: '0.9625rem', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}>
      <input
        type="checkbox"
        checked={isOn}
        onChange={e => toggle(e.target.checked)}
        disabled={disabled}
      />
      {optDisplay}
    </label>
  )
}

function parseCsv(v: unknown): string[] {
  if (v == null || v === '') return []
  if (Array.isArray(v)) return v.map(String)
  return String(v).split(',').map(s => s.trim()).filter(Boolean)
}

// Every form, Vision-imported or not: a field keeps its box; a read-only one
// is shaded.
function fieldStyle(error: string | undefined, disabled: boolean): React.CSSProperties {
  return {
    padding: '7px 10px',
    borderRadius: 4,
    border: error ? '2px solid #ef4444' : '1px solid var(--color-border)',
    // backgroundColor, never the `background` shorthand: the shorthand would
    // wipe the theme's chevron image off a <select> (appTheme.ts).
    backgroundColor: disabled ? 'var(--color-surface-2, var(--color-bg-secondary))' : 'var(--lp-input-bg)',
    color: 'var(--color-text)',
    // Global CSS dims `input:disabled` to opacity 0.5; that makes
    // read-only values hard to read. Keep them at full opacity here
    // so users can still see the captured value clearly.
    opacity: 1,
    WebkitTextFillColor: 'var(--color-text)',  // Safari overrides disabled text color
    fontSize: '0.9625rem',
    outline: 'none',
    boxShadow: error ? '0 0 0 3px rgba(239,68,68,0.15)' : 'none',
  }
}


/** A bool_check drawn as an on/off switch (``edit_mask.style: 'toggle'``) —
 *  Vision's Yes / No toggle. Click or Space/Enter flips it. ``on: null`` is
 *  the neutral state of a field with no value yet (knob centred, muted, "—"):
 *  the first click turns it on. Named by the field caption (``captionId``)
 *  plus its state text. */
function ToggleSwitch({ on, label, disabled, onChange, dbName, captionId, caption, alignEnd }: {
  on: boolean | null
  label: string
  disabled?: boolean
  onChange: (on: boolean) => void
  dbName: string
  captionId?: string
  caption?: string
  /** On its caption's line: pushed to the right edge. */
  alignEnd?: boolean
}) {
  const unset = on === null
  const stateId = `${fieldAnchorId(dbName)}-state`
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...(alignEnd ? { marginLeft: 'auto' } : {}) }}>
      <button
        type="button"
        role="switch"
        aria-checked={unset ? 'mixed' : on}
        {...(captionId
          ? { 'aria-labelledby': `${captionId} ${stateId}` }
          : { 'aria-label': caption ? `${caption}: ${label}` : label })}
        disabled={disabled}
        onClick={() => onChange(unset ? true : !on)}
        data-livepreview-field={dbName}
        style={{
          position: 'relative', width: 38, height: 22, flexShrink: 0,
          borderRadius: 11, padding: 0,
          border: unset ? '1px dashed var(--color-text-secondary, #94a3b8)' : 'none',
          boxSizing: 'border-box',
          background: unset
            ? 'var(--color-surface-2, var(--color-bg-secondary, #f1f5f9))'
            // ON in the project's theme colour (set by the Run shell), else
            // the app theme's accent.
            : on ? 'var(--app-theme-color, var(--color-accent, #2563eb))' : 'var(--color-border, #cbd5e1)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'background 0.15s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: unset ? 2 : 3,
            left: unset ? 10 : on ? 19 : 3,
            width: 16, height: 16, borderRadius: '50%',
            background: unset ? 'var(--color-border, #cbd5e1)' : '#fff',
            boxShadow: unset ? 'none' : '0 1px 2px rgba(0,0,0,0.3)',
            transition: 'left 0.15s',
          }}
        />
      </button>
      <span
        id={stateId}
        style={{ fontSize: 14, color: unset ? 'var(--color-text-secondary)' : 'var(--color-text)' }}
      >{label}</span>
    </div>
  )
}
